import { fetchRegistrationForm, submitRegistrationForm } from '../../../actions/odooFormActions';
import { scrollToFirstFormError } from '../../../utils/scrollUtils';
import Loading from '../../common/Loading';
import NotFound from '../../layout/NotFound';
import Messages from '../Messages';
import Section from '../Section';
import HTMLDisplay from '../formFields/HTMLDisplay';
import arrayMutators from 'final-form-arrays';
import { useCallback, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

const getParameterByName = (name, url = window.location.href) => {
  name = name.replace(/\[/g, '\\$&').replace(/\]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

/**
 * Registration form that can also embed hidden fields into the form, for later use.
 *
 * Hidden fields are used to determine which application form a user should be prompted to complete via email.
 *
 * Once a user completes this form, Odoo sends them an email containing a validation link; this validation link
 * runs via the ValidateUser component, logs the user in automatically, and redirects them on to the relevant
 * application form.
 *
 * The hidden fields passed on to the registration process serve to tell Odoo what sort of form should be created
 * for the user to complete and redirect to.
 *
 * To use, send a ?form=XXXX query parameter to the route that registers this form; the contents will be passed
 * to the server as form value 'form'.
 */

function RegisterFormFields({ handleSubmit, submitting, submitFailed, errors, htmlObjects, sections, isLastPage }) {
  useEffect(() => {
    if (submitFailed && errors) {
      scrollToFirstFormError(errors);
    }
  }, [submitFailed, errors]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Hidden fields initialised from state below */}
      <Field name="sessionId" component="input" type="hidden" />
      <Field name="formType" component="input" type="hidden" />
      <Field name="formId" component="input" type="hidden" />
      <Field name="formPage" component="input" type="hidden" />

      {htmlObjects.map((htmlObject, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <HTMLDisplay key={index} value={htmlObject.value} code={htmlObject.code} cssClass={htmlObject.cssClass} />
      ))}
      {sections.map((section, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Section key={index} data={section} />
      ))}
      <Messages />
      {!isLastPage && (
        <div className="Actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit'}
          </button>
        </div>
      )}
    </form>
  );
}

const RegisterForm = ({ fetchRegistrationForm, submitRegistrationForm, content, config, odooForm, initialValues }) => {
  const params = useParams();
  const navigate = useNavigate();

  const formId = params?.formId || null;
  const formPage = params?.formPage || null;
  const form = getParameterByName('form');

  const shouldHardRedirect = !form && !formId && Boolean(config.addRedirects?.register);

  useEffect(() => {
    if (shouldHardRedirect) {
      window.location.pathname = config.addRedirects.register;
      return;
    }
    fetchRegistrationForm(formId, formPage);
  }, [shouldHardRedirect, config.addRedirects, fetchRegistrationForm, form, formId, formPage]);

  const loginClickHandler = useCallback(() => {
    let loginLink = '/login';
    const form = getParameterByName('form');
    if (form) {
      loginLink += `?redirect=${encodeURIComponent('/start/' + form)}&r=0`;
    }
    navigate(loginLink);
  }, [navigate]);

  if (shouldHardRedirect) return null;
  if (odooForm.isFetching) return <Loading />;
  if (!odooForm.form) return <NotFound />;

  const htmlObjects = odooForm.form.page?.htmlObjects || [];

  const sections = odooForm.form.page?.sections || [];
  const pageCodes = odooForm.form.form.pageCodes;

  const isFirstPage = pageCodes[0].code === odooForm.formPage;
  const isLastPage = pageCodes[pageCodes.length - 1].code === odooForm.formPage;

  return (
    <div className="ContactPage">
      <div className="container content">
        <Messages />
        <div className="bg-light-grey">
          <h3>{content.title}</h3>
          <hr className="pull-left" />
          <div className="clearfix" />
          {content.intro && isFirstPage ? <p>{content.intro}</p> : ''}
          {!isLastPage && content.loginLinkText && (
            <button className="btn btn-link" onClick={loginClickHandler}>
              {content.loginLinkText}
            </button>
          )}
          <Form
            onSubmit={(data) => submitRegistrationForm(config, data, { push: navigate })}
            initialValues={initialValues}
            mutators={arrayMutators}
            render={({ handleSubmit, submitting, submitFailed, errors }) => (
              <RegisterFormFields
                handleSubmit={handleSubmit}
                submitting={submitting}
                submitFailed={submitFailed}
                errors={errors}
                htmlObjects={htmlObjects}
                sections={sections}
                isLastPage={isLastPage}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const initialProps = {
    sessionId: state.user.sessionId,
    formId: state.odooForm.formId,
    formPage: state.odooForm.formPage,
    odooForm: state.odooForm,
    config: state.config,
    content: state.config.content.register,
  };

  if (state.odooForm.form) {
    const initialValues = {
      sessionId: state.user.sessionId,
      formType: state.odooForm.formType,
      formId: state.odooForm.formId,
      formPage: state.odooForm.formPage,
    };

    const sections = state.odooForm.form.page?.sections || [];
    for (const section of sections) {
      for (const group of section.fieldGroups) {
        for (const field of group.fields) {
          if (field.value !== undefined && field.value !== null) {
            initialValues[field.fieldName] = field.value;
          }
        }
      }
    }

    const form = getParameterByName('form');
    if (form) {
      initialValues.application_form_code = form;
    }

    initialProps.initialValues = initialValues;
  }

  return initialProps;
};

const mapDispatchToProps = {
  fetchRegistrationForm,
  submitRegistrationForm,
};

export default connect(mapStateToProps, mapDispatchToProps)(RegisterForm);
